import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BaseCloudscapeForm, BaseFormField, CustomFieldComponentProps } from './base-form';
import Textarea from '@cloudscape-design/components/textarea';
import Multiselect from '@cloudscape-design/components/multiselect';
import { SelectProps } from '@cloudscape-design/components';
import { SpaceBetween } from '@cloudscape-design/components';
import {
    FormField,
    ColumnLayout,
    DatePicker,
    Select,
    Alert,
    TextContent,
    Toggle,
    Box,
    StatusIndicator
} from '@cloudscape-design/components';
import { submissionsApi } from '../../api/Submission';


// Helper function to validate JSON
const isValidJSON = (jsonString: string): boolean => {
  try {
    const parsedPolicy = JSON.parse(jsonString);
    return typeof parsedPolicy === 'object' && parsedPolicy !== null;
  } catch (e) {
    return false;
  }
};

type SearchOption = SelectProps.Option;  // Now it matches Cloudscape's type


interface BaseSearchSelectProps {
    selectedOptions: readonly SelectProps.Option[];
    onSelectionChange: (newSelection: readonly SelectProps.Option[]) => void;
    onSearch: (searchTerm: string) => Promise<SearchOption[]>;
    placeholder?: string;
    errorText?: string;
    loadingText?: string;
    emptyText?: string;
    disabled?: boolean;
}

// Base search component
const BaseSearchSelect: React.FC<BaseSearchSelectProps> = ({
    selectedOptions,
    onSelectionChange,
    onSearch,
    placeholder = "Search...",
    errorText,
    loadingText = "Loading...",
    emptyText = "No results found",
    disabled = false
}) => {
    const [options, setOptions] = useState<SearchOption[]>([]);
    const [loading, setLoading] = useState(false);

    const handleFilterChange = async (event: { detail: { filteringText: string } }) => {
        if (disabled) return;
        
        const searchTerm = event.detail.filteringText;
        setLoading(true);

        try {
            const searchResults = await onSearch(searchTerm);
            setOptions(searchResults);
        } catch (err) {
            console.error('Search error:', err);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Multiselect
            selectedOptions={selectedOptions}
            onChange={event => disabled ? undefined : onSelectionChange(event.detail.selectedOptions)}
            options={options}
            loadingText={loadingText}
            statusType={loading ? 'loading' : 'finished'}
            filteringType="manual"
            onLoadItems={handleFilterChange}
            placeholder={placeholder}
            empty={emptyText}
            errorText={errorText}
            selectedAriaLabel="Selected"
            disabled={disabled}
        />
    );
};



interface DateTimeValue {
    date: string;
    hour: string;
    minute: string;
}

interface DateTimeFieldProps extends CustomFieldComponentProps<DateTimeValue> { }

const DateTimeField: React.FC<DateTimeFieldProps> = ({
    value,
    onChange,
    errorText,
    isSubmitted
}) => {
    // Generate hours options (00-23)
    const hoursOptions = Array.from({ length: 24 }, (_, i) => ({
        label: i.toString().padStart(2, '0'),
        value: i.toString().padStart(2, '0')
    }));

    // Generate minutes options (00-59)
    const minutesOptions = Array.from({ length: 60 }, (_, i) => ({
        label: i.toString().padStart(2, '0'),
        value: i.toString().padStart(2, '0')
    }));

    // Handle individual field changes
    const handleDateChange = (detail: { value: string }) => {
        onChange({
            ...value,
            date: detail.value
        });
    };

    const handleHourChange = (newValue: string) => {
        onChange({
            ...value,
            hour: newValue
        });
    };

    const handleMinuteChange = (newValue: string) => {
        onChange({
            ...value,
            minute: newValue
        });
    };

    // Extract individual error messages if provided
    const errors = typeof errorText === 'object' ? errorText as Record<string, string> : {};

    return (
        <ColumnLayout columns={3} variant="text-grid">
            <FormField
                label="Date"
                errorText={isSubmitted && errors.date}
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
            >
                <DatePicker
                    value={value.date}
                    onChange={({ detail }) => handleDateChange(detail)}
                />
            </FormField>

            <FormField
                label="Hour"
                errorText={isSubmitted && errors.hour}
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
            >
                <Select
                    selectedOption={value.hour ? { value: value.hour, label: value.hour } : null}
                    onChange={({ detail }) => handleHourChange(detail.selectedOption.value || '')}
                    options={hoursOptions}
                    selectedAriaLabel="Selected"
                    placeholder="Select hour"
                />
            </FormField>

            <FormField
                label="Minute"
                errorText={isSubmitted && errors.minute}
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
            >
                <Select
                    selectedOption={value.minute ? { value: value.minute, label: value.minute } : null}
                    onChange={({ detail }) => handleMinuteChange(detail.selectedOption.value || '')}
                    options={minutesOptions}
                    selectedAriaLabel="Selected"
                    placeholder="Select minute"
                />
            </FormField>
        </ColumnLayout>
    );
};


// Policy search specific implementation


// Custom Policy Input Component
interface CustomPolicyInputProps extends CustomFieldComponentProps<string> {
    validationError?: PolicyValidationError | null;
    onBlur?: () => void;
}

const CustomPolicyInput: React.FC<CustomPolicyInputProps> = ({
    value,
    onChange,
    errorText,
    isSubmitted,
    disabled,
    validationError,
    onBlur
}) => {
    return (
        <SpaceBetween direction="vertical" size="xs">
            <Textarea
                value={value}
                onChange={({ detail }) => onChange(detail.value)}
                onBlur={onBlur}
                placeholder={`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": ["s3:GetObject", "s3:ListBucket"],
      "Resource": "arn:aws:s3:::example-bucket/*"
    }
  ]
}`}
                rows={12}
                disabled={disabled}
            />
            
            {validationError && validationError.findings && validationError.findings.length > 0 && (
                <Box variant="div" padding="s" color="text-status-error">
                    <SpaceBetween direction="vertical" size="xs">
                        <TextContent>
                            <h4 style={{ color: 'red' }}>Policy Validation Errors</h4>
                            <ul>
                                {validationError.findings.map((finding, index) => (
                                    <li key={index} style={{ color: 'red' }}>
                                        {finding.findingDetails}
                                    </li>
                                ))}
                            </ul>
                        </TextContent>
                    </SpaceBetween>
                </Box>
            )}
        </SpaceBetween>
    );
};

interface PolicySearchFieldProps extends CustomFieldComponentProps<readonly SearchOption[]> { }

const PolicySearchField: React.FC<PolicySearchFieldProps> = ({
    value,
    onChange,
    errorText,
    isSubmitted,
    disabled
}) => {
    const searchPolicies = async (searchTerm: string): Promise<SearchOption[]> => {
        try {
            const data = await submissionsApi.getPolicies({
                limit: 50,
                search_term: searchTerm
              });

            return data.items.map((policy: any) => ({
                label: policy.title,
                value: policy.id.toString(),
                description: `Type: ${policy.type}`
            }));
        } catch (error) {
            console.error('Error fetching policies:', error);
            return [];
        }
    };

    return (
        <BaseSearchSelect
            selectedOptions={value}
            onSelectionChange={onChange}
            onSearch={searchPolicies}
            placeholder="Search policies..."
            loadingText="Loading policies..."
            emptyText="No matching policies found"
            errorText={isSubmitted ? errorText : undefined}
            disabled={disabled}
        />
    );
};
interface startDateTimeInterface {
    date: string;
    hour: string;
    minute: string;

}
// Form data interface
interface CreateRequestData {
    policies: SearchOption[];
    duration: SelectProps.Option | null;
    startDateTime: startDateTimeInterface;
    notes?: string;
    organization_account_id: SelectProps.Option | null,
    sub_account_id: SelectProps.Option | null,
    is_readonly_access: boolean;
    custom_policy: string;
    policy_type: SelectProps.Option | null;
}

// Main form component
interface PolicyFinding {
    findingType: 'ERROR' | 'SECURITY_WARNING' | 'WARNING' | 'SUGGESTION';
    findingDetails: string;
    locations?: Array<{
        path: any[];
        span: {
            start: { line: number; column: number; offset: number };
            end: { line: number; column: number; offset: number };
        }
    }>;
}

interface PolicyValidationError {
    error_code: number;
    message: string;
    findings?: PolicyFinding[];
}

export const CreateRequestForm: React.FC = () => {
    const [accounts, setAccounts] = useState<Array<{ id: string, account_name: string, target_account_id: string, 
        parent_account_id: string, is_organization: boolean }>>([]);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [policyValidationError, setPolicyValidationError] = useState<PolicyValidationError | null>(null);
    const [isValidating, setIsValidating] = useState(false);

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await submissionsApi.getExternalAccounts({active:true}); 
                const formattedAccounts = data.accounts.map((account: any) => ({
                    id: account.id,
                    account_name: account.account_name,
                    target_account_id: account.target_account_id,
                    parent_account_id: account.parent_account_id,
                    is_organization: account.is_organization

                }));
                setAccounts(formattedAccounts);
            } catch (error) {
                console.error('Error fetching accounts:', error);
            }
        };

        fetchAccounts();
    }, []);

    // Extract error handling into reusable function
    const handleApiError = (error: any, isDryRun: boolean = false) => {
        const errorDetail = error.detail;
        
        let message = "Something went wrong. Please try again.";
        
        if (errorDetail?.error_code === 8) {
            setPolicyValidationError(errorDetail);
            message = isDryRun ? "Policy validation issues detected." : "Custom policy validation failed. Please check the errors below.";
        } else if (errorDetail?.error_code === 9) {
            message = "Policy size exceeds the maximum limit. Please reduce the policy size.";
            setPolicyValidationError(null);
        } else if (errorDetail?.error_code === 10) {
            // Create a proper error object for structure errors to display below the textarea
            setPolicyValidationError({
                error_code: 10,
                message: errorDetail.message || "Policy statements must include Effect, Action, and Resource",
                findings: [{
                    findingType: 'ERROR',
                    findingDetails: errorDetail.message || "Policy statements must include Effect, Action, and Resource"
                }]
            });
            message = isDryRun ? "Policy structure issues detected." : "Policy structure error. Please check the errors below.";
        } else if (errorDetail?.error_code === 1) {
            message = "You have an ongoing or pending access session for the requested account. Please wait for it to complete or cancel/revoke it before creating a new request.";
            setPolicyValidationError(null);
        } else if (errorDetail?.error_code === 2) {
            message = "Policy limits exceeded. Please review your request and try again.";
            setPolicyValidationError(null);
        } else if (errorDetail?.error_code === 7) {
            message = "Target account already has a pending, approved, or ongoing ReadOnlyAccess request.";
            setPolicyValidationError(null);
        } else if (errorDetail?.error_code === 11) {
            message = "Organization policy limit exceeded. Concurrent sessions limit reached.";
            setPolicyValidationError(null);
        } else {
            // Reset any validation errors for other error types
            setPolicyValidationError(null);
        }

        // Always show error messages for both dry runs and actual submissions
        setErrorMessage(message);
        
        return message;
    };

    // Function to prepare submission data
    const prepareSubmissionData = (values: CreateRequestData) => {
        const dateString = `${values.startDateTime.date}T${values.startDateTime.hour}:${values.startDateTime.minute}:00`;
        const date = new Date(dateString);
        
        // Base submission data
        const submissionData: any = {
            organization_account_id: values.organization_account_id?.value,
            sub_account_id: values.sub_account_id?.value,
            duration: Number(values.duration?.value),
            start_datetime: date.toISOString().replace(/\.\d{3}Z$/, ''),
            notes: values.notes
        };
        
        // Handle read-only access
        if (values.is_readonly_access) {
            submissionData.is_readonly_access = true;
        } 
        // If not read-only, handle based on policy type
        else if (values.policy_type && values.policy_type.value === 'managed') {
            submissionData.policies_ids = values.policies.map(p => p.value);
        } 
        else if (values.policy_type && values.policy_type.value === 'custom') {
            // Validate JSON format before parsing
            if (!isValidJSON(values.custom_policy)) {
                // Set policy validation error for JSON errors to display below textarea
                setPolicyValidationError({
                    error_code: 0, // Using 0 for client-side JSON validation errors
                    message: "Invalid JSON format in policy document",
                    findings: [{
                        findingType: 'ERROR',
                        findingDetails: "The policy document contains invalid JSON syntax. Please check for missing commas, brackets, or quotes."
                    }]
                });
                throw new Error("Invalid JSON format in custom policy. Please check the errors below.");
            }

            try {
                submissionData.custom_policy = JSON.parse(values.custom_policy);
            } catch (parseError) {
                // This should not happen since we validated JSON above, but handle it just in case
                setPolicyValidationError({
                    error_code: 0,
                    message: "Failed to parse policy document",
                    findings: [{
                        findingType: 'ERROR',
                        findingDetails: "Unable to parse the policy document. Please check the JSON syntax."
                    }]
                });
                throw new Error("Failed to parse custom policy. Please check the errors below.");
            }
        }

        return submissionData;
    };

    // Function to validate individual field
    const validateField = (field: BaseFormField, value: any, formValues: CreateRequestData): string | undefined => {
        if (field.required && (value === undefined || value === '' || 
            (Array.isArray(value) && value.length === 0))) {
            return `${field.label} is required`;
        }
        return field.validation?.(value, formValues);
    };

    // Function to validate form fields
    const validateFormFields = (values: CreateRequestData): boolean => {
        const newErrors: Record<string, string> = {};
        let isValid = true;
        
        createRequestFields.forEach(field => {
            const error = validateField(field, (values as any)[field.name], values);
            if (error) {
                newErrors[field.name] = error;
                isValid = false;
            }
        });

        if (!isValid) {
            const errorMessages = Object.values(newErrors);
            setErrorMessage(`Please fix the following errors: ${errorMessages.join(', ')}`);
        }
        
        return isValid;
    };

    // Dry run validation function
    const validatePolicies = async (values: CreateRequestData) => {
        // First validate all required form fields
        if (!validateFormFields(values)) {
            return; // Don't proceed with API validation if form validation fails
        }

        // Only validate if we have policy-related data
        if (!values.is_readonly_access && 
            (!values.policy_type || 
             (values.policy_type.value === 'managed' && values.policies.length === 0) ||
             (values.policy_type.value === 'custom' && !values.custom_policy))) {
            return; // Skip validation if no policies to validate
        }

        setIsValidating(true);
        setPolicyValidationError(null); // Clear previous errors
        try {
            const submissionData = prepareSubmissionData(values);
            submissionData.dry_create = true; // Add dry_create flag

            await submissionsApi.createUserAccess(submissionData);
            
            // If we reach here, validation passed
            setPolicyValidationError(null);

        } catch (error: any) {

            handleApiError(error, true); // isDryRun = true
        } finally {
            setIsValidating(false);
        }
    };

    // Handle field changes for policy-related fields (immediate validation for dropdowns)
    const handleFormFieldChange = useCallback((name: string, value: any, formValues: CreateRequestData) => {
        const policyRelatedFields = ['policy_type', 'policies', 'custom_policy', 'is_readonly_access'];
        
        // Clear errors when any policy-related field changes
        if (policyRelatedFields.includes(name)) {
            setErrorMessage(null);
            setPolicyValidationError(null);
        }
        
        const immediateValidationFields = ['policy_type', 'policies', 'is_readonly_access'];
        
        if (immediateValidationFields.includes(name)) {
            // Immediate validation for dropdown selections
            validatePolicies(formValues);
        }
        // Note: custom_policy validation happens onBlur, not onChange
    }, []);

    const createRequestFields: BaseFormField[] = [
        {
            name: 'policy_type',
            label: 'Policy Type',
            type: 'select',
            isCustom: false,
            required: false,
            disabled: (formValues) => formValues?.is_readonly_access === true || isValidating,
            options: [
                { label: 'AWS Managed Policies', value: 'managed' },
                { label: 'Custom Policy', value: 'custom' }
            ],
            placeholder: 'Select policy type',
            validation: (value: SelectProps.Option | null, formValues) => {
                if (!formValues?.is_readonly_access && !value) {
                    return 'Please select a policy type or enable read-only access';
                }
                return undefined;
            },
            description: 'Choose between AWS managed policies or custom policy'
        },
        {
            name: 'policies',
            label: 'AWS Managed Policies',
            type: 'select',
            isCustom: true,
            required: false,
            disabled: (formValues) => formValues?.is_readonly_access === true || formValues?.policy_type?.value !== 'managed' || isValidating,
            customComponent: PolicySearchField,
            validation: (policies: SearchOption[], formValues) => {
                if (!formValues?.is_readonly_access && formValues?.policy_type?.value === 'managed' && policies.length === 0) {
                    return 'Please select at least one policy';
                }
                return undefined;
            },
            description: 'Search and select applicable policies'
        },

        {
            name: 'is_readonly_access',
            label: 'Read Only Access',
            type: 'select',
            isCustom: true,
            required: false,
            customComponent: ({ value, onChange }: CustomFieldComponentProps<boolean>) => (
                <Toggle
                    checked={value}
                    onChange={({ detail }) => {
                        // When enabling read-only, clear policy selections
                        if (detail.checked) {
                            onChange(true);
                        } else {
                            onChange(false);
                        }
                    }}
                />
            ),
            validation: (isReadOnly: boolean, formValues) => {
                // If not read-only and no policy type selected, validation will be handled by policy_type
                return undefined;
            },
            description: 'Enable this for read-only access requests (disables policy selection)'
        },
        {
            name: 'custom_policy',
            label: 'Custom Policy',
            type: 'select',
            isCustom: true,
            required: false,
            disabled: (formValues) => formValues?.is_readonly_access === true || formValues?.policy_type?.value !== 'custom' || isValidating,
            customComponent: (props) => (
                <CustomPolicyInput 
                    {...props} 
                    validationError={policyValidationError}
                />
            ),
            onBlur: (value: string, formValues: Record<string, any>) => {
                // Clear any existing errors before validation
                setErrorMessage(null);
                setPolicyValidationError(null);
                // Trigger immediate validation when user leaves textarea
                validatePolicies(formValues as CreateRequestData);
            },
            validation: (customPolicy: string, formValues) => {
                // Only validate that policy is not empty when custom policy is required
                if (!formValues?.is_readonly_access && formValues?.policy_type?.value === 'custom') {
                    if (!customPolicy) {
                        return 'Please enter a custom policy';
                    }
                }
                return undefined;
            },
            description: 'Enter a valid AWS IAM policy JSON document'
        },
        {
            name: 'organization_account_id',
            label: 'Organization',
            type: 'select',
            isCustom: false,
            required: true,
            options: accounts
                .filter(account => account.is_organization === true)
                .map(account => ({
                label: `${account.account_name} (${account.target_account_id})`,
                value: account.id,
                filterValue: null
            })),
            placeholder: 'Select target account',
            description: 'You can only have one active request per organization',
            validation: (value) => {
                if (!value) {
                    return 'You should choose a target account.'
                }
            }
        },
        {
            name: 'sub_account_id',
            label: 'Sub Account',
            type: 'select',
            isCustom: false,
            required: false,
            dependentOn: {
                field: 'organization_account_id',
              },
            rawOptions: accounts.map(account => ({
                label: `${account.account_name} (${account.target_account_id})`,
                value: account.id,
                filterValue: account.parent_account_id
            })),
            
            options: accounts.map(account => ({
                label: `${account.account_name} (${account.target_account_id})`,
                value: account.id,
                filterValue: account.parent_account_id
            })),
            placeholder: 'Select a sub-account',
            
        },
        {
            name: 'startDateTime',
            label: 'Start Date and Time',
            type: 'select',
            isCustom: true,
            required: true,
            customComponent: DateTimeField,
            validation: (value: { date: string; hour: string; minute: string }) => {
                const errors: Record<string, string> = {};
                console.log(typeof (value));
                if (!value.date) errors.date = 'Date is required';
                if (!value.hour) errors.hour = 'Hour is required';
                if (!value.minute) errors.minute = 'Minute is required';
                if (!value.date || !value.hour || !value.minute) {
                    return 'Valid Start Datetime is required';
                }
            }
        },

        {
            name: 'duration',
            label: 'Duration (hours)',
            type: 'select',
            isCustom: false,
            required: true,
            options: [
                ...Array.from({ length: 12 }, (_, i) => ({
                    label: (i + 1).toString(),
                    value: (i + 1).toString(),
                    filterValue: null
                }))
            ],
            placeholder: 'Select duration',
            description: 'Duration in hours (1-12)',
            validation: (value: SelectProps.Option | null) => {
                if (!value) return 'Duration is required';
                const hours = parseInt(value.value || '0');
                if (isNaN(hours) || hours < 0 || hours > 12) {
                    return 'Duration must be between 1 and 12 hours';
                }
            }
        },
        //   {
        //     name: 'startDate',
        //     label: 'Start Date',
        //     type: 'date',
        //     isCustom: false,
        //     required: true,
        //     placeholder: 'YYYY-MM-DD'
        //   },
        {
            name: 'notes',
            label: 'Notes',
            type: 'text',
            isCustom: false,
            description: 'Optional additional information'
        }
    ];

    const initialValues: CreateRequestData = useMemo(() => ({
        policies: [],
        sub_account_id: null,
        organization_account_id: null,
        duration: null,
        startDateTime: {
            date: new Date().toISOString().split('T')[0],
            hour: new Date().getHours().toString().padStart(2, '0'),
            minute: new Date().getMinutes().toString().padStart(2, '0')
        },
        notes: '',
        is_readonly_access: false,
        custom_policy: '',
        policy_type: null
    }), []);

    const handleSubmit = async (values: CreateRequestData) => {
        try {
            const submissionData = prepareSubmissionData(values);
            // Note: Don't add dry_create flag for actual submission
            
            const response = await submissionsApi.createUserAccess(submissionData);
            
            window.location.href = '/my-applications';

        } catch (error: any) {
            handleApiError(error, false); // isDryRun = false
            return;
        }
    };

    return (
        <SpaceBetween direction="vertical" size="l">
            {errorMessage && (
                <Alert type='error' className="mb-4">
                    {errorMessage}
                </Alert>
            )}

            {isValidating && (
                <Alert type='info' className="mb-4">
                    <StatusIndicator type="loading">Validating policies...</StatusIndicator>
                </Alert>
            )}

            <BaseCloudscapeForm<CreateRequestData>
                fields={createRequestFields}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                onCancel={() => window.location.href = '/my-applications'}
                submitButtonText="Create Request"
                cancelButtonText="Cancel"
                onFieldChange={handleFormFieldChange}
            />
        </SpaceBetween>
    );
};